import { z } from "zod";

/** A deliberately small, immutable hand-off from strategy into production.
 * It is stored with the durable MCP operation rather than becoming a mutable
 * campaign field that can silently change after review. */
export const mcpClaimSchema = z.object({
  claim: z.string().min(1).max(500),
  classification: z.enum(["observed", "brand_provided", "inferred", "generated"]),
  source: z.string().min(1).max(500),
  observedAt: z.string().datetime().nullable(),
  confidence: z.number().min(0).max(1),
  evidenceRefs: z.array(z.string().min(1)).max(20),
  unresolvedQuestions: z.array(z.string().min(1).max(500)).max(20).default([]),
});

export const mcpCampaignBriefSchema = z.object({
  version: z.string().min(1).max(120),
  objective: z.string().min(1).max(2000),
  audience: z.string().min(1).max(2000),
  offer: z.string().min(1).max(2000),
  proof: z.array(z.string().min(1).max(1000)).min(1).max(30),
  claims: z.array(mcpClaimSchema).min(1).max(30),
  kpis: z.array(z.string().min(1).max(300)).min(1).max(20),
  approverRoles: z.array(z.string().min(1).max(100)).min(1).max(10),
}).strict();

export const mcpPolicyPackSchema = z.object({
  prohibitedClaims: z.array(z.string().min(1).max(500)).default([]),
  substantiationRequired: z.array(z.string().min(1).max(500)).default([]),
  sensitiveTraits: z.array(z.string().min(1).max(300)).default([]),
  regulatedCategories: z.array(z.string().min(1).max(300)).default([]),
  rightsConstraints: z.array(z.string().min(1).max(500)).default([]),
  escalationRules: z.array(z.string().min(1).max(500)).default([]),
}).strict().default({});

export type McpCampaignBrief = z.infer<typeof mcpCampaignBriefSchema>;
export type McpPolicyPack = z.infer<typeof mcpPolicyPackSchema>;

export function preflightCampaignBrief(brief: McpCampaignBrief, policy: McpPolicyPack) {
  const normalize = (value: string) => value.toLocaleLowerCase();
  const claimText = brief.claims.map((item) => normalize(item.claim));
  const blockers: string[] = [];
  const requiredReviewers = new Set<string>(brief.approverRoles);
  for (const prohibited of policy.prohibitedClaims) {
    if (claimText.some((claim) => claim.includes(normalize(prohibited)))) blockers.push(`Prohibited claim requires removal: ${prohibited}`);
  }
  for (const claim of brief.claims) {
    if (claim.classification === "inferred" || claim.classification === "generated") {
      blockers.push(`Claim needs human substantiation before launch: ${claim.claim}`);
      requiredReviewers.add("legal_or_brand_reviewer");
    }
    if (!claim.evidenceRefs.length) blockers.push(`Claim has no evidence reference: ${claim.claim}`);
    if (claim.unresolvedQuestions.length) blockers.push(`Claim has unresolved questions: ${claim.claim}`);
  }
  if (policy.substantiationRequired.length && brief.claims.some((claim) => policy.substantiationRequired.some((rule) => normalize(claim.claim).includes(normalize(rule))))) {
    requiredReviewers.add("legal_or_compliance_reviewer");
  }
  if (policy.sensitiveTraits.length || policy.regulatedCategories.length || policy.rightsConstraints.length) requiredReviewers.add("policy_reviewer");
  return {
    status: blockers.length ? "requires_action" as const : "ok" as const,
    launchReady: blockers.length === 0,
    blockers,
    requiredReviewers: [...requiredReviewers],
    remediations: blockers.map((blocker) => ({ action: "resolve_preflight_blocker", description: blocker })),
    provenance: brief.claims.map((claim) => ({
      claim: claim.claim, classification: claim.classification, source: claim.source,
      observed_at: claim.observedAt, confidence: claim.confidence,
      evidence_refs: claim.evidenceRefs, unresolved_questions: claim.unresolvedQuestions,
    })),
  };
}